import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSnapshot} from 'valtio';

import Form, {FormGeneratorHandles} from '@/components/Form';
import {FilePreview, FormItemProps} from '@/components/Form/types';
import {closeNotif, startNotif} from '@/components/DynamicForm/util/notifier';
import Spiral from '@/components/UiElements/Spiral';
import transparencyCheckerboardStyle from '@/components/UiElements/transparencyCheckerboardStyle';
import HelpTooltip from '@/components/UiElements/HelpTooltip';
import {settingsConfigHelp} from '@/components/Settings/configHelp';
import addSetting from '@/services/Settings/addSetting';
import updateSetting from '@/services/Settings/updateSetting';
import upload from '@/services/upload';
import store from '@/store';
import {getCompanyId} from '@/utils/getCompanyId';

const logoSettingKey = 'company_logo';
const brandColorSettingKey = 'brand_color';
const validHexColor = /^#[0-9a-fA-F]{6}$/;
const partialHexColor = /^#[0-9a-fA-F]{0,6}$/;

const logoFormItems: FormItemProps[] = [
    {
        name: logoSettingKey,
        label: 'Company Logo',
        type: 'File',
        placeholder: 'Drag and drop your company logo here',
        help: settingsConfigHelp.companyLogo,
        classNames: {
            container: 'w-full',
            content: '!h-[120px]',
        },
        itemOptions: {
            acceptFormat: 'image/*',
            filePreview: (file: FilePreview) => (
                file.string ? (
                    <div
                        className="flex h-[88px] w-[140px] items-center justify-center overflow-hidden rounded-md border border-gray-200"
                        style={transparencyCheckerboardStyle}
                    >
                        <img
                            src={file.string}
                            alt="Company logo preview"
                            className="h-full w-full object-contain"
                        />
                    </div>
                ) : null
            ),
        },
    },
];

const createStoredLogoPreview = (url: string | null): FilePreview | null => {
    if (!url) return null;

    const cleanUrl = url.split('?')[0];
    const encodedName = cleanUrl.split('/').pop() || 'company-logo';

    return {
        data: null,
        string: url,
        name: decodeURIComponent(encodedName),
        size: 0,
        type: 'image/*',
        url,
        loaded: true,
        uuid: `stored-company-logo-${url}`,
    };
};

const isImageDecodable = (file: File) => new Promise<boolean>((resolve) => {
    if (!file.type.startsWith('image/')) {
        resolve(false);
        return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    const finish = (valid: boolean) => {
        URL.revokeObjectURL(objectUrl);
        resolve(valid);
    };

    image.onload = () => finish(image.naturalWidth > 0 && image.naturalHeight > 0);
    image.onerror = () => finish(false);
    image.src = objectUrl;
});

const saveSetting = async (key: string, value: string | null) => {
    const result: any = await updateSetting({key, value});
    const settingNotFound = result?.response?.data?.error === 'setting_not_found';

    if (settingNotFound) {
        const created = await addSetting({key, value});
        if (created === null) throw new Error(`Failed to create ${key}`);
        return;
    }

    if (result?.response || result instanceof Error) {
        throw result;
    }
};

const CompanyBrandSettings = () => {
    const {companySetting, currentUser} = useSnapshot(store);
    const formRef = useRef<FormGeneratorHandles>(null);
    const activeLogoOperation = useRef<string | null>(null);
    const persistedBrandColorRef = useRef(companySetting.brand_color || '');
    const companyLogo = companySetting.company_logo || null;
    const persistedBrandColor = companySetting.brand_color || '';
    const selectedCompanyId = currentUser?.selectedCompany?.id || null;

    const [logoSaving, setLogoSaving] = useState(false);
    const [colorSaving, setColorSaving] = useState(false);
    const [brandColorInput, setBrandColorInput] = useState(persistedBrandColor);

    const logoDefaultValues = useMemo(() => ({
        [logoSettingKey]: createStoredLogoPreview(companyLogo),
    }), [companyLogo]);

    useEffect(() => {
        formRef.current?.setData(logoDefaultValues);
    }, [logoDefaultValues]);

    useEffect(() => {
        persistedBrandColorRef.current = persistedBrandColor;
        setBrandColorInput(persistedBrandColor);
    }, [persistedBrandColor]);

    const saveLogoValue = useCallback(async (filePreview: FilePreview | null) => {
        if (filePreview?.url === store.companySetting.company_logo) return;
        if (filePreview?.uuid && activeLogoOperation.current === filePreview.uuid) return;

        const operationId = filePreview?.uuid || `remove-${Date.now()}`;
        const companyIdAtStart = getCompanyId();
        activeLogoOperation.current = operationId;
        setLogoSaving(true);
        const notificationId = startNotif(filePreview ? 'Uploading company logo...' : 'Removing company logo...');

        try {
            if (!filePreview) {
                await saveSetting(logoSettingKey, null);
                store.companySetting.company_logo = null;
                closeNotif(notificationId, 'Company logo removed successfully.');
                return;
            }

            if (!filePreview.data || !(await isImageDecodable(filePreview.data))) {
                throw new Error('The selected file is not a valid image.');
            }

            const uploadedUrl = await upload({
                file: filePreview.data,
                fileName: filePreview.data.name,
                folderName: 'brand',
            });

            if (!uploadedUrl) throw new Error('Company logo upload failed.');
            if (getCompanyId() !== companyIdAtStart) {
                throw new Error('The selected company changed before the logo could be saved.');
            }

            await saveSetting(logoSettingKey, uploadedUrl);
            store.companySetting.company_logo = uploadedUrl;
            closeNotif(notificationId, 'Company logo saved successfully.');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to save company logo.';
            closeNotif(notificationId, message, {type: 'error'});
            formRef.current?.setData({
                [logoSettingKey]: createStoredLogoPreview(store.companySetting.company_logo),
            });
        } finally {
            if (activeLogoOperation.current === operationId) {
                activeLogoOperation.current = null;
                setLogoSaving(false);
            }
        }
    }, []);

    const handleLogoFormChange = useCallback((formData: Record<string, any>) => {
        void saveLogoValue((formData[logoSettingKey] as FilePreview | null) || null);
    }, [saveLogoValue]);

    const saveBrandColor = useCallback(async (color: string | null) => {
        const normalizedColor = color || '';
        if (normalizedColor === persistedBrandColorRef.current) return;
        if (normalizedColor && !validHexColor.test(normalizedColor)) return;

        setColorSaving(true);
        const notificationId = startNotif(normalizedColor ? 'Saving brand color...' : 'Removing brand color...');

        try {
            await saveSetting(brandColorSettingKey, normalizedColor || null);
            store.companySetting.brand_color = normalizedColor || null;
            persistedBrandColorRef.current = normalizedColor;
            closeNotif(
                notificationId,
                normalizedColor ? 'Brand color saved successfully.' : 'Brand color removed successfully.',
            );
        } catch {
            setBrandColorInput(persistedBrandColorRef.current);
            closeNotif(notificationId, 'Failed to save brand color.', {type: 'error'});
        } finally {
            setColorSaving(false);
        }
    }, []);

    const handleColorTextChange = (value: string) => {
        if (value === '' || partialHexColor.test(value)) {
            setBrandColorInput(value);
        }
    };

    const commitColorText = () => {
        if (brandColorInput === '') {
            void saveBrandColor(null);
            return;
        }

        if (validHexColor.test(brandColorInput)) {
            void saveBrandColor(brandColorInput);
            return;
        }

        setBrandColorInput(persistedBrandColorRef.current);
    };

    return (
        <div className="grid grid-cols-1 gap-xl xl:grid-cols-2">
            <section className="relative flex min-h-[210px] flex-col gap-md rounded-md border border-gray-300 p-md shadow-md">
                <div className={logoSaving ? 'pointer-events-none opacity-60' : ''}>
                    <Form
                        key={selectedCompanyId || 'company-logo'}
                        ref={formRef}
                        items={logoFormItems}
                        defaultValues={logoDefaultValues}
                        onChange={handleLogoFormChange}
                    />
                </div>
                {logoSaving && (
                    <span className="flex items-center gap-sm text-text-sm text-gray-400">
                        <Spiral />
                        Uploading and saving logo...
                    </span>
                )}
            </section>

            <HelpTooltip help={settingsConfigHelp.brandColor} title="Brand Color">
                <section className="flex min-h-[210px] flex-col gap-md rounded-md border border-gray-300 p-md shadow-md">
                    <div>
                        <p className="text-text-md text-gray-900">Brand Color</p>
                        <p className="pt-xs text-text-sm text-gray-400">
                            Select the company brand color or enter a six-digit hex value.
                        </p>
                    </div>

                    <div className="flex items-center gap-sm">
                        <label
                            className="relative h-[45px] w-[56px] shrink-0 cursor-pointer overflow-hidden rounded-md border border-gray-300 shadow-xs transition-all hover:border-primary-500 active:shadow-focus"
                            style={{backgroundColor: validHexColor.test(brandColorInput) ? brandColorInput : 'transparent'}}
                        >
                            <span className="sr-only">Select brand color</span>
                            <input
                                type="color"
                                aria-label="Select brand color"
                                value={validHexColor.test(brandColorInput) ? brandColorInput : '#000000'}
                                disabled={colorSaving}
                                onChange={(event) => {
                                    const color = event.target.value;
                                    setBrandColorInput(color);
                                    void saveBrandColor(color);
                                }}
                                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                            />
                        </label>

                        <input
                            type="text"
                            aria-label="Brand color hex value"
                            value={brandColorInput}
                            disabled={colorSaving}
                            placeholder="#000000"
                            maxLength={7}
                            onChange={(event) => handleColorTextChange(event.target.value)}
                            onBlur={commitColorText}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') event.currentTarget.blur();
                                if (event.key === 'Escape') {
                                    setBrandColorInput(persistedBrandColorRef.current);
                                    event.currentTarget.blur();
                                }
                            }}
                            className="h-[45px] min-w-0 flex-grow rounded-md border border-gray-300 px-md font-mono text-title-md uppercase text-gray-900 shadow-xs outline-none transition-all placeholder:text-gray-300 hover:border-primary-500 focus:border-primary-500 focus:shadow-focus disabled:cursor-wait disabled:bg-gray-50"
                        />
                    </div>

                    {colorSaving && (
                        <span className="flex items-center gap-sm text-text-sm text-gray-400">
                            <Spiral />
                            Saving brand color...
                        </span>
                    )}
                </section>
            </HelpTooltip>
        </div>
    );
};

export default CompanyBrandSettings;
